// src/pages/ConfirmBooking/ConfirmBookingDesktop.jsx
import React from "react";
import {
  PALETTE,
  getNiceDate,
  SectionCard,
  Label,
  DatePill,
  AcPill,
  SeatPill,
  TimeGreenPill,
  HoldCountdown,
  RowInput,
  PassengerRow,
} from "./core";

const BookingSteps = () => null;

/* ─── Inline premium sub-components ─────────────────────────────────── */

/** Glassy trip overview card — replaces the plain SectionCard */
const TripCard = ({
  bus,
  date,
  departureTime,
  selectedBoardingPoint,
  selectedDroppingPoint,
  selectedSeats,
  lockVersion,
  clientId,
  setHoldExpired,
  releaseSeats,
  handleHoldExpiredRedirect,
}) => {
  const boarding =
    typeof selectedBoardingPoint === "object"
      ? selectedBoardingPoint
      : { point: selectedBoardingPoint, time: "" };
  const dropping =
    typeof selectedDroppingPoint === "object"
      ? selectedDroppingPoint
      : { point: selectedDroppingPoint, time: "" };

  return (
    <div
      className="rounded-3xl overflow-hidden mt-4"
      style={{
        background: "#FFFFFF",
        border: `1px solid rgba(199,74,80,0.12)`,
        boxShadow:
          "0 2px 8px rgba(199,74,80,0.06), 0 12px 40px rgba(16,24,40,0.07)",
      }}
    >
      {/* Header band */}
      <div
        className="px-6 py-4 flex items-center justify-between gap-4"
        style={{
          background:
            "linear-gradient(100deg, #C74A50 0%, #D4575D 55%, #B8404A 100%)",
        }}
      >
        <div className="min-w-0">
          <p
            className="text-white font-bold text-base leading-tight truncate"
            style={{ letterSpacing: "-0.02em" }}
          >
            {bus?.name || bus?.busName || "Bus"}
          </p>
          <p className="text-white/70 text-xs mt-0.5">
            {bus?.busType || "AC"} &middot;{" "}
            {(selectedSeats || []).length}{" "}
            {selectedSeats?.length === 1 ? "Seat" : "Seats"} selected
          </p>
        </div>

        {/* Hold countdown badge */}
        <div className="shrink-0 text-right">
          <p className="text-[10px] font-bold uppercase tracking-widest text-white/60 mb-1">
            Seat Hold
          </p>
          <HoldCountdown
            key={`hold-${lockVersion}-${(selectedSeats || []).map(String).join(",")}`}
            busId={bus?._id}
            date={date}
            departureTime={departureTime}
            seats={selectedSeats}
            clientId={clientId}
            lockVersion={lockVersion}
            onExpire={handleHoldExpiredRedirect}
          />
        </div>
      </div>

      {/* Route bar */}
      <div
        className="grid grid-cols-[1fr_auto_1fr] items-stretch"
        style={{ borderBottom: "1px solid #F3F4F6" }}
      >
        {/* Boarding */}
        <div className="px-6 py-4">
          <p
            className="text-[10px] font-bold uppercase tracking-widest mb-1.5"
            style={{ color: "#059669" }}
          >
            Boarding
          </p>
          <p
            className="font-bold text-sm capitalize"
            style={{ color: PALETTE.text }}
          >
            {boarding.point || "—"}
          </p>
          {boarding.time && (
            <p
              className="text-xs font-semibold mt-1 tabular-nums"
              style={{ color: "#059669" }}
            >
              {boarding.time}
            </p>
          )}
        </div>

        {/* Centre divider with date */}
        <div className="flex flex-col items-center justify-center px-5 py-3 gap-1.5">
          <div
            className="w-px flex-1"
            style={{
              background:
                "linear-gradient(180deg, transparent, #E5E7EB 40%, transparent)",
            }}
          />
          <div
            className="px-3 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap"
            style={{
              background: "#FFF9DB",
              color: "#92400E",
              border: "1px solid #FDE68A",
            }}
          >
            {getNiceDate(date)}
          </div>
          <div
            className="w-px flex-1"
            style={{
              background:
                "linear-gradient(180deg, #E5E7EB 40%, transparent)",
            }}
          />
        </div>

        {/* Dropping */}
        <div className="px-6 py-4 text-right">
          <p
            className="text-[10px] font-bold uppercase tracking-widest mb-1.5"
            style={{ color: PALETTE.primary }}
          >
            Dropping
          </p>
          <p
            className="font-bold text-sm capitalize"
            style={{ color: PALETTE.text }}
          >
            {dropping.point || "—"}
          </p>
          {dropping.time && (
            <p
              className="text-xs font-semibold mt-1 tabular-nums"
              style={{ color: PALETTE.primary }}
            >
              {dropping.time}
            </p>
          )}
        </div>
      </div>

      {/* Seats row */}
      <div className="px-6 py-3.5 flex items-center gap-2 flex-wrap">
        <span
          className="text-[10px] font-bold uppercase tracking-widest mr-1"
          style={{ color: PALETTE.textMuted }}
        >
          Seats
        </span>
        {(selectedSeats || []).map((s) => (
          <SeatPill key={s}>Seat {s}</SeatPill>
        ))}
      </div>
    </div>
  );
};

/** Premium fare summary card */
const FareCard = ({ prices, holdExpired }) => (
  <div
    className="rounded-3xl overflow-hidden"
    style={{
      background: "#FFFFFF",
      border: `1px solid ${PALETTE.borderSoft}`,
      boxShadow: PALETTE.shadowMd,
    }}
  >
    <div
      className="px-5 py-3.5"
      style={{ borderBottom: `1px solid ${PALETTE.borderSoft}` }}
    >
      <p
        className="text-sm font-bold"
        style={{ color: PALETTE.text, letterSpacing: "-0.01em" }}
      >
        Fare Summary
      </p>
    </div>
    <div className="px-5 py-4 space-y-3">
      <div className="flex justify-between items-center text-sm">
        <span style={{ color: PALETTE.textSubtle }}>Subtotal</span>
        <span
          className="tabular-nums font-semibold"
          style={{ color: PALETTE.text }}
        >
          Rs. {prices.basePrice.toFixed(2)}
        </span>
      </div>
      <div className="flex justify-between items-center text-sm">
        <span style={{ color: PALETTE.textSubtle }}>Convenience Fee</span>
        <span
          className="tabular-nums font-semibold"
          style={{ color: PALETTE.text }}
        >
          Rs. {prices.convenienceFee.toFixed(2)}
        </span>
      </div>
      <div
        className="h-px"
        style={{
          background: `linear-gradient(90deg, transparent, ${PALETTE.border}, transparent)`,
        }}
      />
      <div className="flex justify-between items-center">
        <span className="font-bold text-sm" style={{ color: PALETTE.text }}>
          Total Payable
        </span>
        <span
          className="tabular-nums text-xl font-extrabold"
          style={{ color: PALETTE.primary }}
        >
          Rs. {prices.total.toFixed(2)}
        </span>
      </div>
      {holdExpired && (
        <div
          className="rounded-xl px-3 py-2 text-xs font-semibold"
          style={{
            background: "#FEF2F2",
            color: "#991B1B",
            border: "1px solid #FECACA",
          }}
        >
          Seat hold expired — go back and reselect seats.
        </div>
      )}
    </div>
  </div>
);

/* ─── Card wrapper matching other section cards ───────────────────── */
const PremiumCard = ({ title, subtitle, children }) => (
  <div
    className="rounded-3xl overflow-hidden mt-4"
    style={{
      background: "#FFFFFF",
      border: `1px solid ${PALETTE.borderSoft}`,
      boxShadow: PALETTE.shadowMd,
    }}
  >
    {(title || subtitle) && (
      <div
        className="px-5 py-4"
        style={{ borderBottom: `1px solid ${PALETTE.borderSoft}` }}
      >
        {title && (
          <p
            className="text-sm font-bold"
            style={{ color: PALETTE.text, letterSpacing: "-0.01em" }}
          >
            {title}
          </p>
        )}
        {subtitle && (
          <p className="text-xs mt-0.5" style={{ color: PALETTE.textMuted }}>
            {subtitle}
          </p>
        )}
      </div>
    )}
    <div className="px-5 py-4">{children}</div>
  </div>
);

/* ─── Desktop bottom payment bar — redBus-style ───────────────────── */
const DesktopBottomPayBar = ({
  total,
  canPay,
  isSubmitting,
  handleSubmit,
}) => (
  <div
    className="hidden lg:block fixed bottom-0 left-0 right-0 z-40"
    style={{
      background: PALETTE.surface,
      borderTop: `1px solid ${PALETTE.border}`,
      boxShadow: "0 -10px 30px rgba(16,24,40,0.06)",
    }}
  >
    <div className="max-w-6xl mx-auto px-4 py-3">
      <div className="flex items-center justify-end gap-6">
        <div className="min-w-0 text-right">
          <p
            className="text-[11px] font-semibold leading-none"
            style={{ color: PALETTE.textSubtle }}
          >
            Amount
          </p>
          <p
            className="text-[11px] mt-1"
            style={{ color: PALETTE.textMuted }}
          >
            (Tax included)
          </p>
        </div>

        <div
          className="text-3xl font-extrabold tabular-nums leading-none"
          style={{ color: PALETTE.text, minWidth: "160px" }}
        >
          Rs. {total.toFixed(2)}
        </div>

        <button
          type="button"
          disabled={!canPay}
          onClick={() => handleSubmit({ preventDefault: () => {} })}
          className="shrink-0 px-12 py-3.5 rounded-full text-white font-bold text-base transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            minWidth: "280px",
            background: canPay
              ? `linear-gradient(100deg, ${PALETTE.primary} 0%, ${PALETTE.primaryDark} 100%)`
              : "#D1D5DB",
            boxShadow: canPay
              ? "0 4px 12px rgba(199,74,80,0.18)"
              : "none",
            letterSpacing: "-0.01em",
          }}
        >
          {isSubmitting ? "Processing…" : "Continue booking"}
        </button>
      </div>
    </div>
  </div>
);

/* ─── Main component ─────────────────────────────────────────────────── */

const ConfirmBookingDesktop = ({
  pageTopRef,
  bus,
  date,
  departureTime,
  selectedBoardingPoint,
  selectedDroppingPoint,
  selectedSeats,
  prices,
  errors,
  holdExpired,
  termsAccepted,
  cameBackFromGateway,
  lockVersion,
  clientId,
  isSubmitting,
  showLeaveConfirm,
  closeLeaveConfirm,
  confirmLeaveToResults,
  form,
  onChangeForm,
  blurValidateField,
  passengers,
  setPassengerName,
  setPassengerAge,
  setPassengerGender,
  blurValidatePassenger,
  setHoldExpired,
  releaseSeats,
  toggleTerms,
  goBackToResults,
  openLeaveConfirm,
  handleSubmit,
  handleHoldExpiredRedirect,
}) => {
  const hasErrors =
    errors.name ||
    errors.mobile ||
    errors.nic ||
    errors.email ||
    Object.keys(errors.passengers || {}).length ||
    errors.terms ||
    holdExpired;

  const canPay = termsAccepted && !holdExpired && !isSubmitting;

  return (
    <div
      ref={pageTopRef}
      className="min-h-screen text-[16px] md:text-base"
      style={{ background: PALETTE.bg }}
    >
      {/* ── Sticky top bar ── */}
      <div
        className="sticky top-0 z-30 backdrop-blur-md"
        style={{
          background: "rgba(255,255,255,0.92)",
          paddingTop: "env(safe-area-inset-top)",
          borderBottom: "1px solid #E5E7EB",
          boxShadow: "0 2px 16px rgba(16,24,40,0.04)",
        }}
      >
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p
              className="text-base font-bold leading-tight"
              style={{ color: PALETTE.text, letterSpacing: "-0.02em" }}
            >
              Confirm Booking
            </p>
            <p className="text-xs mt-0.5" style={{ color: PALETTE.textMuted }}>
              {bus?.from} &rarr; {bus?.to}
              {date ? ` · ${getNiceDate(date)}` : ""}
              {departureTime ? ` at ${departureTime}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={openLeaveConfirm}
            className="hidden sm:inline-flex items-center px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 active:scale-[0.97]"
            style={{
              background: "#F3F4F6",
              color: "#111827",
              border: "1px solid #E5E7EB",
            }}
          >
            Back to Results
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 pb-40 lg:pb-32">
        {/* Payment failed banner */}
        {cameBackFromGateway && (
          <div
            className="mt-4 rounded-2xl px-4 py-3 text-sm font-medium flex items-start gap-2"
            style={{
              background: "#FEF2F2",
              color: "#991B1B",
              border: "1px solid #FECACA",
            }}
          >
            <span className="shrink-0">⚠</span>
            Payment was cancelled or failed. Review your details and try again.
          </div>
        )}

        {/* Field errors banner */}
        {hasErrors && (
          <div
            className="mt-4 rounded-2xl px-4 py-3 text-sm font-medium flex items-start gap-2"
            style={{
              background: "#FEF2F2",
              color: "#991B1B",
              border: "1px solid #FECACA",
            }}
          >
            <span className="shrink-0">⚠</span>
            {holdExpired
              ? "Your seat hold has expired. Please go back and reselect seats."
              : "Please correct the highlighted fields below."}
          </div>
        )}

        {/* Full-width layout */}
        <div className="mt-2 space-y-4">
          {/* Trip card */}
          <TripCard
            bus={bus}
            date={date}
            departureTime={departureTime}
            selectedBoardingPoint={selectedBoardingPoint}
            selectedDroppingPoint={selectedDroppingPoint}
            selectedSeats={selectedSeats}
            lockVersion={lockVersion}
            clientId={clientId}
            setHoldExpired={setHoldExpired}
            releaseSeats={releaseSeats}
            handleHoldExpiredRedirect={handleHoldExpiredRedirect}
          />

          <FareCard prices={prices} holdExpired={holdExpired} />

          {/* Contact Details */}
          <PremiumCard
            title="Contact Details"
            subtitle="We'll send your ticket confirmation here"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <RowInput
                id="name"
                name="name"
                label="Full Name"
                value={form.name}
                onChange={onChangeForm}
                onBlur={() => blurValidateField("name")}
                autoComplete="name"
                enterKeyHint="next"
                placeholder="e.g., Ramesh Perera"
                required
                error={errors.name}
                maxLength={80}
              />
              <RowInput
                id="mobile"
                name="mobile"
                label="Mobile Number"
                type="tel"
                value={form.mobile}
                onChange={onChangeForm}
                onBlur={() => blurValidateField("mobile")}
                autoComplete="tel"
                inputMode="tel"
                enterKeyHint="next"
                placeholder="e.g., 07XXXXXXXX"
                required
                error={errors.mobile}
                maxLength={11}
                pattern="^0\\d{9,10}$"
              />
              <RowInput
                id="nic"
                name="nic"
                label="NIC / Passport"
                value={form.nic}
                onChange={onChangeForm}
                onBlur={() => blurValidateField("nic")}
                autoComplete="off"
                enterKeyHint="next"
                placeholder="e.g., 200012345678"
                required
                error={errors.nic}
                maxLength={20}
              />
              <RowInput
                id="email"
                name="email"
                label="Email Address"
                type="email"
                value={form.email}
                onChange={onChangeForm}
                onBlur={() => blurValidateField("email")}
                autoComplete="email"
                inputMode="email"
                enterKeyHint="done"
                placeholder="e.g., ramesh@email.com"
                required
                error={errors.email}
                maxLength={100}
              />
            </div>
          </PremiumCard>

          {/* Passenger Details */}
          <PremiumCard
            title="Passenger Details"
            subtitle="Enter details exactly as on travel documents"
          >
            <div className="space-y-4">
              {passengers.map((p, idx) => (
                <PassengerRow
                  key={p.seat}
                  p={p}
                  index={idx}
                  onName={setPassengerName}
                  onAge={setPassengerAge}
                  onGender={setPassengerGender}
                  errorsForSeat={errors.passengers?.[p.seat]}
                  onBlurName={(seat) => blurValidatePassenger(seat, "name")}
                  onBlurAge={(seat) => blurValidatePassenger(seat, "age")}
                />
              ))}
            </div>
          </PremiumCard>

          {/* Terms */}
          <PremiumCard title="Terms &amp; Conditions">
            <p
              className="text-sm leading-6"
              style={{ color: PALETTE.textSubtle }}
            >
              All passenger and journey details must be correct before payment.
              By continuing you agree to our{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noreferrer"
                className="font-semibold underline underline-offset-2"
                style={{ color: PALETTE.primary }}
              >
                Terms &amp; Conditions
              </a>{" "}
              and{" "}
              <a
                href="/refund-policy"
                target="_blank"
                rel="noreferrer"
                className="font-semibold underline underline-offset-2"
                style={{ color: PALETTE.primary }}
              >
                Refund Policy
              </a>
              .
            </p>

            <label
              className="mt-4 flex items-start gap-3 rounded-2xl px-4 py-3.5 cursor-pointer transition-all duration-200"
              style={{
                background: termsAccepted
                  ? PALETTE.primarySoft
                  : PALETTE.surfaceAlt,
                border: `1.5px solid ${
                  errors.terms
                    ? "#FECACA"
                    : termsAccepted
                    ? "rgba(199,74,80,0.25)"
                    : PALETTE.border
                }`,
              }}
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 accent-[#C74A50]"
                checked={termsAccepted}
                onChange={toggleTerms}
                required
              />
              <span
                className="text-sm leading-6"
                style={{ color: PALETTE.text }}
              >
                I confirm all entered details are correct and I agree to the
                Terms &amp; Conditions.
              </span>
            </label>

            {errors.terms && (
              <p
                className="mt-2 text-xs font-medium"
                style={{ color: "#B91C1C" }}
              >
                {errors.terms}
              </p>
            )}
          </PremiumCard>

        </div>
      </div>

      {/* ── Desktop floating pill CTA ── */}
      <DesktopBottomPayBar
        total={prices.total}
        canPay={canPay}
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
      />

      {/* ── Leave confirm modal ── */}
      {showLeaveConfirm && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/45 backdrop-blur-[5px]"
            onClick={closeLeaveConfirm}
          />

          <div
            className="relative w-full max-w-md overflow-hidden rounded-[30px] shadow-2xl"
            style={{
              background: PALETTE.surface,
              border: `1px solid ${PALETTE.borderSoft}`,
              boxShadow:
                "0 24px 70px rgba(16,24,40,0.24), 0 2px 10px rgba(16,24,40,0.08)",
            }}
          >
            <div className="px-6 pt-4 pb-2">
              <div
                className="mx-auto h-1.5 w-12 rounded-full"
                style={{ background: "#E5E7EB" }}
              />
            </div>

            <div className="px-7 pt-3 pb-7 text-center">
              <div
                className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl"
                style={{
                  background: "linear-gradient(180deg, #FFF1F2 0%, #FFE4E6 100%)",
                  border: "1px solid #FFE4E6",
                  boxShadow: "0 10px 22px rgba(199,74,80,0.10)",
                }}
              >
                <span className="text-2xl" aria-hidden="true">
                  ↩
                </span>
              </div>

              <h3
                className="text-[22px] leading-tight font-extrabold"
                style={{ color: PALETTE.text, letterSpacing: "-0.035em" }}
              >
                Return to search results?
              </h3>

              <p
                className="mt-3 text-[15px] leading-7"
                style={{ color: PALETTE.textSubtle }}
              >
                Your seat preference and details are saved on this page.
              </p>

              <div
                className="mt-5 rounded-2xl px-4 py-3 text-left text-sm leading-6"
                style={{
                  background: PALETTE.surfaceAlt,
                  border: `1px solid ${PALETTE.border}`,
                  color: PALETTE.textSubtle,
                }}
              >
                Going back will release your held seats immediately, so another
                passenger may book them.
              </div>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={closeLeaveConfirm}
                  className="w-full px-5 py-4 rounded-full text-[15px] font-bold transition-all duration-200 active:scale-[0.98]"
                  style={{
                    background: "#FFFFFF",
                    color: PALETTE.text,
                    border: `1.5px solid ${PALETTE.border}`,
                    boxShadow: "0 6px 14px rgba(16,24,40,0.04)",
                  }}
                >
                  Continue booking
                </button>

                <button
                  type="button"
                  onClick={confirmLeaveToResults}
                  className="w-full px-5 py-4 rounded-full text-[15px] font-bold text-white transition-all duration-200 active:scale-[0.98]"
                  style={{
                    background: `linear-gradient(100deg, ${PALETTE.primary} 0%, ${PALETTE.primaryDark} 100%)`,
                    boxShadow: "0 10px 24px rgba(199,74,80,0.24)",
                  }}
                >
                  View other buses
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Mobile sticky bottom bar ── */}
      <div
        className="sm:hidden fixed bottom-0 left-0 right-0 z-40 px-4 py-3"
        style={{
          background: PALETTE.surface,
          borderTop: `1px solid ${PALETTE.border}`,
          paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)",
        }}
      >
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <p
              className="text-[10px] uppercase tracking-widest font-bold"
              style={{ color: PALETTE.textMuted }}
            >
              Payable
            </p>
            <p
              className="text-lg font-extrabold tabular-nums"
              style={{ color: PALETTE.primary }}
            >
              Rs. {prices.total.toFixed(2)}
            </p>
          </div>
          <button
            type="button"
            disabled={!canPay}
            onClick={() => handleSubmit({ preventDefault: () => {} })}
            className="px-6 py-3 rounded-2xl text-white font-bold text-sm transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: canPay
                ? `linear-gradient(100deg, ${PALETTE.primary} 0%, ${PALETTE.primaryDark} 100%)`
                : "#D1D5DB",
              boxShadow: canPay ? "0 4px 16px rgba(199,74,80,0.3)" : "none",
            }}
          >
            {isSubmitting ? "Processing…" : "Proceed to Pay"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmBookingDesktop;



